import { useToast } from "@au-schein-de/au-ui-library";
import type { Cart } from "@dransay/api/models";
import * as Sentry from "@sentry/react-router";
import type React from "react";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useLoaderData, useNavigate } from "react-router";
import { useCurrency } from "~/hooks/use-currency-price.hook";
import { useError } from "~/hooks/use-error.hook";
import type { SessionUser } from "~/tooling/session";
import { captureCheckoutStepCompletedOnceForCart } from "~/tooling/telemetry/checkout-step-once";
import {
	CHECKOUT_STEP_TO_TELEMETRY_CHECKOUT_STEP,
	resolveCheckoutTelemetryFlowFromPrescription,
} from "~/tooling/telemetry/checkout-step-sequence";
import type { ErrorTelemetry } from "~/tooling/telemetry/error-telemetry";
import { ErrorCodeNames } from "~/utils/errors/error-codes";
import type { Route } from "../../routes/+types/checkout";
import type { VersionedCart } from "../cart/cart.context.types";
import type {
	Checkout,
	CheckoutCart,
	CheckoutInput,
	CheckoutStep,
} from "./checkout.schemas";
import { CheckoutStore } from "./checkout.store";
import { refreshCheckoutCart } from "./checkout-cart-refresh.logic";
import { isStoredCheckoutCartStale } from "./checkout-cart-sync.logic";
import { resolveCompletedStepTitle } from "./checkout-completed-step";
import {
	CHECKOUT_STEP_NAMES,
	CHECKOUT_STEP_NAMES_BASE,
	checkoutStepsByType,
	headerStepperData,
} from "./constants";
import { isCrossSellStepOffered } from "./cross-sell-availability.logic";
import { getCheckoutType, getDiagnoseSteps } from "./helpers";
import type { QuestionnairePrefillPayload } from "./map-questionnaire-to-checkout";
import { useCheckoutExitUrl } from "./use-checkout-exit-url";

/**
 * The stable message `api.checkout.order` returns when the customer's own
 * stored record fails patient-data validation — in practice an address the ETL
 * sync blanked (JUPITER-WEB-692). Matched here so the dialog can offer the
 * address form instead of sending the patient back to the first checkout step,
 * which cannot fix a gap in server-side data.
 */
export const INCOMPLETE_PATIENT_DATA = "Incomplete patient data";

const ERROR_MESSAGES = {
	CheckoutStoreNotInitialized: "CheckoutStore is not initialized",
	UserUnauthenticated: "User unauthenticated",
};

interface CheckoutContextType {
	checkoutData: Checkout;
	checkoutSteps: Readonly<CheckoutStep[]>;
	enableQuestionnairePrefill: boolean;
	enableOtcPurchase: boolean;
	/**
	 * Whether the cross-sell step is reached at all — see FR-040.
	 *
	 * The raw `enableCheckoutOtcCrossSell` is deliberately not exposed beside it.
	 * This feature's defect was four call sites each deciding from the flag, and a
	 * context offering both would let the next one pick whichever it saw first.
	 */
	isCrossSellOffered: boolean;
	headerSteps: Array<{
		label: string;
		pathname: string;
	}>;
	setStepData: <K extends keyof Omit<CheckoutInput, "expireAt">>(
		key: K,
		data: CheckoutInput[K],
	) => Promise<Checkout | null>;
	updateCheckoutStep: (
		currentStep: CheckoutStep,
		nextStep: CheckoutStep,
		skipSteps?: CheckoutStep[],
		options?: { replaceHistory?: boolean },
	) => Promise<Checkout | null>;
	applyQuestionnairePrefillAndGoToSummary: (
		payload: QuestionnairePrefillPayload,
	) => Promise<Checkout | null>;
	clearCheckoutData: () => Promise<null | undefined>;
	showError: (
		recoverable?: boolean,
		errorMessage?: string,
		errorCode?: string,
		telemetry?: ErrorTelemetry,
	) => void;
}

function isValidContext(
	context: Omit<CheckoutContextType, "checkoutData"> & {
		checkoutData?: Checkout | null;
	},
): context is CheckoutContextType {
	return !!context.checkoutData;
}

const CheckoutContext = createContext<CheckoutContextType | undefined>(
	undefined,
);

export const CheckoutProvider = ({
	children,
	cart,
	enableQuestionnairePrefill = false,
	enableCheckoutOtcCrossSell = false,
	enableOtcCrossSellForCombinedPayment = false,
	enableOtcPurchase = false,
}: React.PropsWithChildren<{
	cart: Cart;
	enableQuestionnairePrefill?: boolean;
	enableCheckoutOtcCrossSell?: boolean;
	enableOtcCrossSellForCombinedPayment?: boolean;
	enableOtcPurchase?: boolean;
}>) => {
	const { t } = useTranslation(["checkout", "cart"]);
	const { toast } = useToast();
	const navigate = useNavigate();
	const getCheckoutExitUrl = useCheckoutExitUrl();
	const { showError, hideError } = useError();
	const currency = useCurrency();
	const storeRef = useRef<CheckoutStore | null>(null);

	const [checkoutData, setCheckoutData] = useState<Checkout | null>(null);
	const [initialized, setInitialized] = useState<boolean>(false);

	const { user } = useLoaderData<Route.ComponentProps["loaderData"]>();

	const checkoutSteps = getCheckoutSteps();

	// Derived from the cart prop rather than `checkoutData.cart`: the stored copy
	// can lag the basket, and a stale vendor would decide this wrongly.
	const isCrossSellOffered = isCrossSellStepOffered({
		cart,
		enableCheckoutOtcCrossSell,
		enableOtcCrossSellForCombinedPayment,
	});

	useEffect(() => {
		// IndexedDB store initialization has to be done in the Browser context
		const checkout = new CheckoutStore();
		storeRef.current = checkout;
	}, []);

	const getCheckoutData = useCallback(async () => {
		const store = storeRef.current;

		if (!store || !user?.externalId) {
			setCheckoutData(null);
			return null;
		}

		const data = await store.get(user.externalId);
		setCheckoutData(data);
		return data;
	}, [user?.externalId]);

	const getHeaderSteps = useCallback(() => {
		const headerSteps = headerStepperData.map((step) => {
			if (
				checkoutData?.prescription &&
				!checkoutData.prescription.prescriptionUpload &&
				checkoutData.prescription.prescriptionOrderType === "follow-up" &&
				step.pathname === CHECKOUT_STEP_NAMES_BASE.diagnose
			) {
				return {
					label: t("steps.caseHistory"),
					pathname: step.pathname,
				};
			}
			return { label: t(step.labelKey), pathname: step.pathname };
		});

		return headerSteps;
	}, [checkoutData?.prescription, t]);

	const setStepData = useCallback(
		async <K extends keyof Omit<CheckoutInput, "expireAt">>(
			key: K,
			data: CheckoutInput[K],
		) => {
			const store = storeRef.current;

			if (!store) {
				throw new Error(ERROR_MESSAGES.CheckoutStoreNotInitialized);
			}

			if (!user?.externalId) {
				throw new Error(ERROR_MESSAGES.UserUnauthenticated);
			}

			await store.set(user.externalId, key, data);
			return getCheckoutData();
		},
		[user?.externalId, getCheckoutData],
	);

	const updateCheckoutStep = useCallback(
		async (
			currentStep: CheckoutStep,
			nextStep: CheckoutStep,
			skipSteps?: CheckoutStep[],
			options?: { replaceHistory?: boolean },
		) => {
			const checkoutSteps = {
				currentStep: nextStep,
				completedSteps: [
					...new Set([
						...(checkoutData?.checkoutSteps.completedSteps ?? []),
						currentStep,
						...(skipSteps ?? []),
					]),
				],
			};

			const data = await setStepData("checkoutSteps", checkoutSteps);

			if (currentStep !== nextStep) {
				const commerce = {
					cart: checkoutData?.cart,
					currency,
					flow: resolveCheckoutTelemetryFlowFromPrescription(
						checkoutData?.prescription,
					),
				};
				const completedStepTitle = resolveCompletedStepTitle(
					currentStep,
					checkoutData?.cart,
				);
				if (completedStepTitle) {
					captureCheckoutStepCompletedOnceForCart({
						telemetryStep: completedStepTitle,
						cartId: checkoutData?.cart?.cartId,
						commerce,
					});
				}

				// @note: Workaround for https://github.com/remix-run/react-router/issues/11240
				// The URL would change but the page content would not update
				// This issue is reproducible on very slow connection (Network throttling set to 3G) or in E2E
				// It partially fixes the issue JUP-1634 but not completely
				// Both calls stay. The first decides history: an entry per step, so
				// back walks the checkout one step at a time (FR-034), except for a
				// step passing the customer straight through, which consumes its own
				// entry rather than leaving one that walks them forward again. The
				// second forces UI navigation and is always a replace, so it never
				// adds one.
				await navigate(nextStep, {
					replace: Boolean(options?.replaceHistory),
				});
				await navigate(nextStep, { replace: true }); // force UI navigation
			}

			return data;
		},
		[
			checkoutData?.cart,
			checkoutData?.checkoutSteps.completedSteps,
			checkoutData?.prescription,
			currency,
			setStepData,
			navigate,
		],
	);

	const applyQuestionnairePrefillAndGoToSummary = useCallback(
		async (payload: QuestionnairePrefillPayload) => {
			// Mirrors the awareness step's own branch: with the flag on, cross-sell
			// is a real destination the customer must still go through, not a step
			// to mark skipped and jump past.
			const completedSteps = [
				...new Set([
					...(checkoutData?.checkoutSteps.completedSteps ?? []),
					...getQuestionnairePrefillCompletedSteps(
						payload.prescription.prescriptionOrderType,
					),
					...(isCrossSellOffered ? [] : [CHECKOUT_STEP_NAMES.crossSell]),
				]),
			];
			const nextStep = isCrossSellOffered
				? CHECKOUT_STEP_NAMES.crossSell
				: CHECKOUT_STEP_NAMES.summary;

			await setStepData("prescription", payload.prescription);
			await setStepData("diagnose", payload.diagnose);
			await setStepData("awareness", payload.awareness);
			const data = await setStepData("checkoutSteps", {
				currentStep: nextStep,
				completedSteps,
			});

			captureCheckoutStepCompletedOnceForCart({
				telemetryStep:
					CHECKOUT_STEP_TO_TELEMETRY_CHECKOUT_STEP[
						CHECKOUT_STEP_NAMES.prescriptionOrder
					],
				cartId: checkoutData?.cart?.cartId,
				commerce: {
					cart: checkoutData?.cart,
					currency,
					flow: resolveCheckoutTelemetryFlowFromPrescription(
						payload.prescription,
					),
				},
			});

			await navigate(nextStep);
			await navigate(nextStep, { replace: true });

			return data;
		},
		[
			checkoutData?.cart,
			checkoutData?.checkoutSteps.completedSteps,
			currency,
			isCrossSellOffered,
			navigate,
			setStepData,
		],
	);

	const clearCheckoutData = useCallback(() => {
		const store = storeRef.current;

		if (!store) {
			throw new Error(ERROR_MESSAGES.CheckoutStoreNotInitialized);
		}

		if (!user?.externalId) {
			throw new Error(ERROR_MESSAGES.UserUnauthenticated);
		}

		return store.delete(user.externalId);
	}, [user?.externalId]);

	const showErrorHandler = useCallback(
		(
			recoverable = false,
			errorMessage?: string,
			errorCode = ErrorCodeNames.BAD_REQUEST,
			telemetry?: ErrorTelemetry,
		) => {
			const closeButton = {
				action: hideError,
				label: t("close", { ns: "common" }),
			};

			if (errorCode === ErrorCodeNames.UNPROCESSABLE_CONTENT) {
				showError({
					message: t("errors.cartValidationFailed", { ns: "common" }),
					telemetry: telemetry ?? { category: "checkout_failure" },
					primaryButton: {
						action: async () => {
							await clearCheckoutData();
							hideError();
							navigate(getCheckoutExitUrl());
						},
						label: t("productsList", { ns: "common" }),
					},
					secondaryButton: closeButton,
				});
				return;
			}

			if (errorMessage === INCOMPLETE_PATIENT_DATA) {
				// Reload rather than navigate: the summary's own delivery-address
				// step opens in edit mode when the loader reports the address
				// incomplete, and that loader value is read once into state. A
				// fresh load is what puts the form in front of the patient.
				showError({
					message: t("errors.patientDataIncomplete", { ns: "common" }),
					telemetry: {
						category: "validation",
						codes: ["INCOMPLETE_PATIENT_DATA"],
					},
					primaryButton: {
						action: () => {
							hideError();
							navigate(0);
						},
						label: t("errors.patientDataIncomplete.action", { ns: "common" }),
					},
					secondaryButton: closeButton,
				});
				return;
			}

			if (!recoverable) {
				showError({
					message: t("errors.checkoutIncomplete", { ns: "common" }),
					telemetry: telemetry ?? { category: "unknown" },
					primaryButton: {
						action: () => {
							hideError();
							navigate(checkoutSteps[0]);
						},
						label: t("checkout", { ns: "cart" }),
					},
					secondaryButton: closeButton,
				});
			} else {
				showError({
					message: errorMessage,
					telemetry: telemetry ?? { category: "unknown" },
					primaryButton: closeButton,
				});
			}
		},
		[
			checkoutSteps,
			navigate,
			hideError,
			showError,
			t,
			clearCheckoutData,
			getCheckoutExitUrl,
		],
	);

	useEffect(() => {
		async function initialize() {
			try {
				if (!storeRef.current) return;

				let data = await getCheckoutData();

				const shouldInitializeCheckout =
					Boolean(user?.externalId) &&
					(!data?.cart || isStoredCheckoutCartStale(data.cart, cart));

				if (shouldInitializeCheckout) {
					try {
						await initializeCheckout({ user, cart });
						data = await getCheckoutData();
					} catch (error) {
						Sentry.captureException(error, {
							extra: { context: "checkout-auto-initialize" },
						});
						toast({
							title: t("errors.checkoutFailed", { ns: "cart" }),
							variant: "destructive",
						});
						navigate(getCheckoutExitUrl());
						return;
					}
				}

				if (data?.cart && !shouldInitializeCheckout && user?.externalId) {
					const refreshedCart = refreshCheckoutCart(data.cart, cart);
					if (refreshedCart !== data.cart) {
						await storeRef.current.set(user.externalId, "cart", refreshedCart);
						data = await getCheckoutData();
					}
				}

				if (!data) {
					navigate("/");
					return;
				}

				setInitialized(true);
			} catch (error) {
				Sentry.captureException(error, {
					extra: { context: "checkout-initialization" },
				});
				toast({
					title: t("errors.checkoutFailed", { ns: "cart" }),
					variant: "destructive",
				});
				navigate(getCheckoutExitUrl());
			}
		}

		initialize();
	}, [navigate, getCheckoutData, user, cart, toast, t, getCheckoutExitUrl]);

	const context = useMemo(
		() => ({
			checkoutData,
			checkoutSteps,
			enableQuestionnairePrefill,
			enableOtcPurchase,
			isCrossSellOffered,
			headerSteps: getHeaderSteps(),
			setStepData,
			updateCheckoutStep,
			applyQuestionnairePrefillAndGoToSummary,
			clearCheckoutData,
			showError: showErrorHandler,
		}),
		[
			checkoutData,
			checkoutSteps,
			enableQuestionnairePrefill,
			enableOtcPurchase,
			isCrossSellOffered,
			getHeaderSteps,
			setStepData,
			updateCheckoutStep,
			applyQuestionnairePrefillAndGoToSummary,
			clearCheckoutData,
			showErrorHandler,
		],
	);

	if (!storeRef.current || !initialized || !isValidContext(context)) {
		return null;
	}

	return (
		<CheckoutContext.Provider value={context}>
			{children}
		</CheckoutContext.Provider>
	);
};

export const useCheckout = () => {
	const context = useContext(CheckoutContext);

	if (context === undefined) {
		throw new Error("useCheckout must be used within a CheckoutProvider");
	}

	return context;
};

// Functions related with Checkout but invoked outside of the CheckoutContext

const getCheckoutSteps = () => {
	const checkoutType = getCheckoutType();
	const checkoutSteps = checkoutStepsByType[checkoutType];

	return checkoutSteps;
};

const getQuestionnairePrefillCompletedSteps = (
	prescriptionOrderType: QuestionnairePrefillPayload["prescription"]["prescriptionOrderType"],
): CheckoutStep[] => [
	CHECKOUT_STEP_NAMES.prescriptionOrder,
	...getDiagnoseSteps(prescriptionOrderType).map(
		(step) => `${CHECKOUT_STEP_NAMES_BASE.diagnose}/${step}` as CheckoutStep,
	),
	CHECKOUT_STEP_NAMES.awareness,
];

export const initializeCheckout = async ({
	user,
	cart,
}: {
	user: SessionUser;
	cart: VersionedCart;
}): Promise<string> => {
	const checkout = new CheckoutStore();

	if (!user) {
		throw new Error(ERROR_MESSAGES.UserUnauthenticated);
	}

	if (!cart.deliveryMethodUuid || !cart.vendor || !cart.items.length) {
		throw new Error("Cart is empty, cannot start checkout");
	}

	if (cart.remainingCapacity < 0) {
		throw new Error("Cart limit exceeded, cannot start checkout");
	}

	const checkoutSteps = getCheckoutSteps();
	const firstStepPathname = checkoutSteps[0];
	const existing = await checkout.get(user.externalId);

	const shouldPreserveQuestionnaire =
		existing?.prescription && !existing.prescription.prescriptionUpload;

	const preservedQuestionnaire = shouldPreserveQuestionnaire
		? {
				prescription: existing.prescription,
				...(existing.diagnose && { diagnose: existing.diagnose }),
				...(existing.awareness && { awareness: existing.awareness }),
			}
		: {};

	// The delivery step's per-package confirmations survive a re-initialisation.
	// Until OTC-41 gives the cart a method per group, choosing one writes it to
	// the STORED cart alone, and deliveryMethodUuid is part of the staleness
	// fingerprint -- so the step makes its own checkout look stale, and a re-init
	// that dropped this key would erase the confirmations the customer had just
	// made, between the delivery step and the summary that reports them.
	//
	// Entries that no longer hold are not this function's problem: the step
	// filters them through dropInvalidConfirmations on entry (FR-013).
	const preservedDelivery = existing?.delivery
		? { delivery: existing.delivery }
		: {};

	await checkout.initialize(user.externalId, {
		// Cast to CheckoutCart for compatibility as z.input<z.coerce.number()> still expects a number event though string should be fine.
		// It should get resolved in zod v4.
		// https://github.com/colinhacks/zod/issues/2519#issuecomment-2070921897
		cart: cart as unknown as CheckoutCart,
		checkoutSteps: {
			currentStep: firstStepPathname,
			completedSteps: [],
		},
		...preservedQuestionnaire,
		...preservedDelivery,
	});

	return firstStepPathname;
};

export const clearCheckoutData = async (user: SessionUser) => {
	try {
		const checkout = new CheckoutStore();
		await checkout.delete(user.externalId);
	} catch (error) {
		Sentry.addBreadcrumb({
			category: "checkout-payment",
			message: "Failed to clear checkout data",
			data: { error },
		});
		Sentry.captureMessage("Failed to clear checkout data");
	}
};
